/**
 * Route Analytics Utility
 * Tracks route loading performance and errors
 */

import * as Sentry from '@sentry/react';

/**
 * Route load metrics
 */
export interface RouteLoadMetrics {
  path: string;
  loadTime: number;
  dataSize?: number;
  timestamp: number;
  success: boolean;
  error?: string;
}

/**
 * In-memory storage for route metrics
 */
class RouteMetricsStore {
  private metrics: RouteLoadMetrics[] = [];
  private readonly maxMetrics = 100; // Keep last 100 metrics

  add(metric: RouteLoadMetrics): void {
    this.metrics.push(metric);

    // Keep only the last N metrics
    if (this.metrics.length > this.maxMetrics) {
      this.metrics = this.metrics.slice(-this.maxMetrics);
    }
  }

  getAll(): RouteLoadMetrics[] {
    return [...this.metrics];
  }

  getByPath(path: string): RouteLoadMetrics[] {
    return this.metrics.filter((m) => m.path === path);
  }

  getAverageLoadTime(path?: string): number {
    const relevantMetrics = path
      ? this.getByPath(path)
      : this.metrics.filter((m) => m.success);

    if (relevantMetrics.length === 0) return 0;

    const sum = relevantMetrics.reduce((acc, m) => acc + m.loadTime, 0);
    return sum / relevantMetrics.length;
  }

  getSuccessRate(path?: string): number {
    const relevantMetrics = path ? this.getByPath(path) : this.metrics;

    if (relevantMetrics.length === 0) return 0;

    const successCount = relevantMetrics.filter((m) => m.success).length;
    return (successCount / relevantMetrics.length) * 100;
  }

  getSlowestRoutes(limit: number = 5): Array<{
    path: string;
    avgLoadTime: number;
  }> {
    const pathGroups = new Map<string, RouteLoadMetrics[]>();

    // Group metrics by path
    this.metrics.forEach((metric) => {
      if (!pathGroups.has(metric.path)) {
        pathGroups.set(metric.path, []);
      }
      pathGroups.get(metric.path)!.push(metric);
    });

    // Calculate average load time per path
    const pathAverages = Array.from(pathGroups.entries()).map(
      ([path, metrics]) => {
        const avgLoadTime =
          metrics.reduce((sum, m) => sum + m.loadTime, 0) / metrics.length;
        return { path, avgLoadTime };
      }
    );

    // Sort by avgLoadTime descending and return top N
    return pathAverages
      .sort((a, b) => b.avgLoadTime - a.avgLoadTime)
      .slice(0, limit);
  }

  clear(): void {
    this.metrics = [];
  }
}

// Singleton instance
export const routeMetricsStore = new RouteMetricsStore();

/**
 * Track successful route load
 */
export function trackRouteLoad(
  path: string,
  loadTime: number,
  dataSize?: number
): void {
  const metric: RouteLoadMetrics = {
    path,
    loadTime,
    dataSize,
    timestamp: Date.now(),
    success: true,
  };

  // Add to store
  routeMetricsStore.add(metric);

  // Send to Sentry as breadcrumb
  Sentry.addBreadcrumb({
    category: 'route-load',
    message: `Route loaded: ${path}`,
    level: 'info',
    data: {
      loadTime,
      dataSize,
    },
  });

  // Log slow routes (>2s)
  if (loadTime > 2000) {
    console.warn(
      `[Route Analytics] Slow route detected: ${path} (${loadTime}ms)`
    );

    // Send to Sentry as warning
    Sentry.captureMessage(`Slow route: ${path}`, {
      level: 'warning',
      tags: {
        route: path,
        loadTime: `${loadTime}ms`,
      },
    });
  }

  // Log to console in development
  if (import.meta.env.DEV) {
    console.log(
      `[Route Analytics] ${path} loaded in ${loadTime}ms`,
      dataSize ? `(${dataSize} bytes)` : ''
    );
  }
}

/**
 * Track route load error
 */
export function trackRouteError(path: string, error: Error | string): void {
  const errorMessage = error instanceof Error ? error.message : error;

  const metric: RouteLoadMetrics = {
    path,
    loadTime: 0,
    timestamp: Date.now(),
    success: false,
    error: errorMessage,
  };

  // Add to store
  routeMetricsStore.add(metric);

  // Send to Sentry
  Sentry.captureException(error, {
    tags: {
      route: path,
      type: 'route-load-error',
    },
    contexts: {
      route: {
        path,
        timestamp: metric.timestamp,
      },
    },
  });

  // Log to console
  console.error(`[Route Analytics] Error loading route ${path}:`, error);
}

/**
 * Get route performance summary
 */
export function getRoutePerformanceSummary(): {
  totalRoutes: number;
  averageLoadTime: number;
  successRate: number;
  slowestRoutes: Array<{ path: string; avgLoadTime: number }>;
} {
  const allMetrics = routeMetricsStore.getAll();

  return {
    totalRoutes: new Set(allMetrics.map((m) => m.path)).size,
    averageLoadTime: routeMetricsStore.getAverageLoadTime(),
    successRate: routeMetricsStore.getSuccessRate(),
    slowestRoutes: routeMetricsStore.getSlowestRoutes(5),
  };
}

/**
 * Start timing a route load
 */
export function startRouteTimer(): () => number {
  const startTime = performance.now();

  return () => {
    return performance.now() - startTime;
  };
}

/**
 * Calculate data size from response
 */
export function calculateDataSize(data: unknown): number {
  try {
    return JSON.stringify(data).length;
  } catch {
    return 0;
  }
}
